// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Operational screens for Бриф (hybrid IA): Обзор / Очередь / Календарь /
// Каналы. Wired to LIVE data from the v2-promotion-publisher worker via the
// Phase-2 promo layer (promo/postsApi + promo/channelsApi). Reads are SOFT
// (bearer, no logout on 401). NO demo/mock data: when a read is not live the
// screen shows an honest "не удалось связаться с сервером" note + an empty
// state. The Бриф design language is preserved verbatim.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { getQueueStage, listPosts } from './promo/postsApi';
import {
  getConnections,
  getHealth,
} from './promo/channelsApi';
import type {
  ChannelReadiness as PromoReadiness,
  ConnectionSummary,
  Platform as PromoPlatform,
  PromotionPost,
} from './promo/types';
import { ConnectSheet, type SheetPlatform } from './screens/ChannelsManage';
import { BestTimeHeatmap, PostMetricsPanel, RescheduleSheet } from './screens/analytics';
import {
  buildCalendar,
  deriveOverview,
  PLATFORM_GLYPH,
  PLATFORM_LABEL,
  READINESS_META,
  STAGE_META,
  STATUS_DOT,
  type ChannelInfo,
  type ChannelReadiness,
  type OverviewData,
  type Platform,
  type PlatformStatus,
  type PostPlatform,
  type PromoPost,
  type QueueStage,
} from './opsData';
import { C, mono, SANS } from './theme';

// ── Loaded wrapper (soft-read result) ────────────────────────────

interface Loaded<T> {
  data: T;
  live: boolean;
}

// ── Live → view-model adapters ───────────────────────────────────
//
// The promo layer returns the raw worker shapes (PromotionPost,
// ConnectionSummary, ChannelReadiness). The screens render the lighter
// opsData.ts view models (PromoPost, ChannelInfo, OverviewData), so we map
// here. `getQueueStage` (client-derived) supplies the queue bucket exactly as
// the old page did; we reuse it rather than re-deriving.

// The 8 social platforms readiness reports on, in the legacy iteration order.
const SOCIAL_PLATFORMS: Platform[] = [
  'telegram',
  'twitter',
  'instagram',
  'threads',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
];

// Platforms with a connection slot that the Каналы screen surfaces (8 social +
// reddit + producthunt). openai/gemini are AI providers, not publishing channels.
const CHANNEL_PLATFORMS: Platform[] = [
  ...SOCIAL_PLATFORMS,
  'reddit',
  'producthunt',
];

function asStatus(value: unknown): PlatformStatus {
  return value === 'published' || value === 'publishing' || value === 'failed' || value === 'skipped' || value === 'pending'
    ? value
    : 'pending';
}

/** PromotionPost (worker) → PromoPost (view model). Stage uses getQueueStage. */
function mapPost(post: PromotionPost): PromoPost {
  const statusObj = post.status ?? ({} as PromotionPost['status']);
  const resultsObj = post.results ?? ({} as PromotionPost['results']);
  const platforms: PostPlatform[] = CHANNEL_PLATFORMS.filter((p) => statusObj[p] != null).map((p) => {
    const result = resultsObj[p];
    return {
      platform: p,
      status: asStatus(statusObj[p]),
      link: result?.external_url,
    };
  });

  const source = post.source ?? { title: '', body: '' };
  const title =
    (typeof source.title === 'string' && source.title) ||
    (typeof source.body === 'string' && source.body.slice(0, 60)) ||
    'Без названия';

  return {
    id: post.id,
    title,
    stage: getQueueStage(post),
    platforms: platforms.length ? platforms : [{ platform: 'telegram', status: 'pending' }],
    scheduledAt: post.scheduled_at ?? null,
    createdAt: post.created_at ?? null,
  };
}

/**
 * Loads posts as view models with a live flag. `listPosts` swallows failures
 * into `[]`, so we lean on the readiness health read (which carries `live`) to
 * decide whether the session is alive. When NOT live we return an empty list —
 * the screen then shows an honest offline note + empty state (no demo data).
 */
async function loadPosts(): Promise<Loaded<PromoPost[]>> {
  const [posts, health] = await Promise.all([listPosts(200), getHealth()]);
  if (!health.live) {
    return { data: [], live: false };
  }
  return { data: posts.map(mapPost), live: true };
}

// Map a connection status + readiness into the screen's readiness bucket.
function deriveReadiness(conn: ConnectionSummary | undefined, ready: PromoReadiness | undefined): ChannelReadiness {
  if (ready?.ready || conn?.status === 'connected') return 'ready';
  if (ready?.status === 'unsupported') return 'manual';
  return 'setup';
}

// Choose the "via" descriptor shown under the platform name.
function deriveVia(platform: Platform, ready: PromoReadiness | undefined): ChannelInfo['via'] {
  if (ready?.method === 'buffer' || ready?.buffer_ready) return 'buffer';
  if (platform === 'telegram' || platform === 'twitter') return 'official';
  if (ready?.status === 'unsupported') return 'manual';
  return 'cookies';
}

// Build the human-readable detail line from readiness reasons / account.
function deriveDetail(conn: ConnectionSummary | undefined, ready: PromoReadiness | undefined): string {
  if (ready?.account) return ready.account;
  if (ready?.reasons && ready.reasons.length) return ready.reasons[0];
  if (ready?.warnings && ready.warnings.length) return ready.warnings[0];
  if (conn?.status === 'expired') return 'Сессия истекла';
  if (conn?.status === 'error') return 'Ошибка подключения';
  if (conn?.status === 'connected') return 'Подключено';
  return 'Не подключён';
}

/** ConnectionSummary + ChannelReadiness → ChannelInfo (view model). */
function mapChannels(
  connections: ConnectionSummary[],
  readiness: PromoReadiness[],
): ChannelInfo[] {
  const connByPlatform = new Map<string, ConnectionSummary>();
  for (const c of connections) connByPlatform.set(c.platform, c);
  const readyByPlatform = new Map<string, PromoReadiness>();
  for (const r of readiness) readyByPlatform.set(r.platform, r);

  return CHANNEL_PLATFORMS.map((platform) => {
    const conn = connByPlatform.get(platform);
    const ready = readyByPlatform.get(platform as PromoPlatform);
    return {
      platform,
      readiness: deriveReadiness(conn, ready),
      via: deriveVia(platform, ready),
      detail: deriveDetail(conn, ready),
    };
  });
}

/** Loads channels as view models, live flag from the soft reads. */
async function loadChannels(): Promise<Loaded<ChannelInfo[]>> {
  const [connections, health] = await Promise.all([getConnections(), getHealth()]);
  const live = connections.live && health.live;
  if (!live) {
    return { data: [], live: false };
  }
  return { data: mapChannels(connections.data, health.data), live: true };
}

// ── Shared chrome ────────────────────────────────────────────────

function ScreenHeader({ title, tag, mobile, right }: { title: string; tag?: string; mobile?: boolean; right?: ReactNode }) {
  if (mobile) return null; // on mobile the section title lives in the top bar
  return (
    <div style={{ height: 56, flexShrink: 0, borderBottom: `1px solid ${C.line4}`, display: 'flex', alignItems: 'center', gap: 10, padding: mobile ? '0 16px' : '0 22px' }}>
      <div style={{ fontWeight: 600, fontSize: 14.5 }}>{title}</div>
      {tag && <div style={mono({ fontSize: 10, color: C.faint, border: `1px solid ${C.line}`, borderRadius: 6, padding: '2px 7px' })}>{tag}</div>}
      {right && <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>{right}</div>}
    </div>
  );
}

function OfflineNote({ live }: { live: boolean }) {
  if (live) return null;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: C.chipBg, border: `1px solid ${C.line3}`, borderRadius: 9, padding: '6px 11px', marginBottom: 16 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#c1442e', flexShrink: 0 }} />
      <span style={{ fontSize: 12.5, color: C.sub }}>Не удалось связаться с сервером. Проверьте подключение.</span>
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.faint2, padding: '24px 0' }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 0.2, 0.4].map((d, i) => (
          <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: C.faint, display: 'inline-block', animation: `briefPulse 1.2s infinite ${d}s` }} />
        ))}
      </div>
      <span style={{ fontSize: 13 }}>{label}</span>
    </div>
  );
}

function ScreenScroll({ children, mobile }: { children: ReactNode; mobile?: boolean }) {
  return (
    <div className="brief-scroll" style={{ flex: 1, overflowY: 'auto', padding: mobile ? '20px 16px 28px' : '24px 0' }}>
      <div style={{ maxWidth: mobile ? '100%' : 860, margin: '0 auto', padding: mobile ? 0 : '0 24px', animation: 'briefUp .3s ease both' }}>
        {children}
      </div>
    </div>
  );
}

function MonoLabel({ children }: { children: ReactNode }) {
  return <div style={mono({ fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint, margin: '0 0 10px' })}>{children}</div>;
}

// ── Platform chips ───────────────────────────────────────────────

function PlatformPills({ post }: { post: PromoPost }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {post.platforms.map((pl, i) => (
        <span key={i} title={PLATFORM_LABEL[pl.platform]} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: C.sub, background: C.white, border: `1px solid ${C.line3}`, borderRadius: 7, padding: '3px 8px' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_DOT[pl.status], flexShrink: 0 }} />
          <span style={mono({ fontSize: 9.5, color: C.muted })}>{PLATFORM_GLYPH[pl.platform]}</span>
        </span>
      ))}
    </div>
  );
}

const rowActionBtn = {
  background: 'none',
  border: `1px solid ${C.line3}`,
  borderRadius: 8,
  padding: '5px 11px',
  fontFamily: SANS,
  fontSize: 12.5,
  color: C.sub,
  cursor: 'pointer',
} as const;

function PostRow({ post, mobile, onClick, onReschedule, metricsEnabled }: {
  post: PromoPost;
  mobile?: boolean;
  onClick?: () => void;
  onReschedule?: () => void;
  metricsEnabled?: boolean;
}) {
  const [showMetrics, setShowMetrics] = useState(false);
  const when = post.scheduledAt
    ? new Date(post.scheduledAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : 'без даты';
  const hasActions = !!onReschedule || !!metricsEnabled;
  return (
    <div onClick={onClick} style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: mobile ? '13px 14px' : '14px 16px', background: C.white, cursor: onClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: mobile ? 14 : 14.5, fontWeight: 600, color: C.ink2, letterSpacing: '-.01em' }}>{post.title}</div>
        <span style={mono({ fontSize: 10, color: STAGE_META[post.stage].tone, border: `1px solid ${C.line3}`, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 })}>{STAGE_META[post.stage].label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <PlatformPills post={post} />
        <span style={mono({ fontSize: 10.5, color: C.faint, marginLeft: 'auto' })}>{when}</span>
      </div>
      {hasActions && (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {metricsEnabled && (
            <button style={rowActionBtn} onClick={() => setShowMetrics((v) => !v)}>
              {showMetrics ? 'Скрыть метрики' : 'Метрики'}
            </button>
          )}
          {onReschedule && (
            <button style={rowActionBtn} onClick={onReschedule}>Перенести</button>
          )}
        </div>
      )}
      {showMetrics && <PostMetricsPanel postId={post.id} />}
    </div>
  );
}

// ── Обзор ────────────────────────────────────────────────────────

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint: string }) {
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '15px 16px', background: C.white }}>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1 }}>{value}</div>
      <div style={mono({ fontSize: 10, color: C.faint, marginTop: 8 })}>{hint}</div>
    </div>
  );
}

export function OverviewScreen({ mobile, onOpenQueue }: { mobile?: boolean; onOpenQueue?: () => void }) {
  const [state, setState] = useState<Loaded<OverviewData> | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [posts, channels] = await Promise.all([loadPosts(), loadChannels()]);
      if (!alive) return;
      setState({
        data: deriveOverview(posts.data, channels.data),
        live: posts.live && channels.live,
      });
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      <ScreenHeader title="Обзор" tag="состояние" mobile={mobile} />
      <ScreenScroll mobile={mobile}>
        {!state ? (
          <Loading label="Загружаю обзор…" />
        ) : (
          <>
            <OfflineNote live={state.live} />
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10, marginBottom: 22 }}>
              <StatCard label="Требуют внимания" value={state.data.attention} hint="ошибки, черновики" />
              <StatCard label="Запланировано" value={state.data.scheduled} hint="будущие публикации" />
              <StatCard label="Публикуется" value={state.data.publishing} hint="сейчас в работе" />
              <StatCard label="Готовые каналы" value={`${state.data.channelsReady}/${state.data.channelsTotal}`} hint="подключения" />
            </div>
            <MonoLabel>Рабочая очередь</MonoLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {state.data.recent.length ? (
                state.data.recent.map((p) => <PostRow key={p.id} post={p} mobile={mobile} onClick={onOpenQueue} />)
              ) : (
                <div style={{ fontSize: 14, color: C.muted }}>Пока нет постов в очереди.</div>
              )}
            </div>
          </>
        )}
      </ScreenScroll>
    </>
  );
}

// ── Очередь ──────────────────────────────────────────────────────

const STAGES: QueueStage[] = ['attention', 'scheduled', 'publishing', 'published'];

export function QueueScreen({ mobile }: { mobile?: boolean }) {
  const [state, setState] = useState<Loaded<PromoPost[]> | null>(null);
  const [sheet, setSheet] = useState<PromoPost | null>(null);

  const reload = useCallback(() => {
    let alive = true;
    loadPosts().then((r) => alive && setState(r));
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => reload(), [reload]);

  return (
    <>
      <ScreenHeader title="Очередь" tag="посты" mobile={mobile} />
      <ScreenScroll mobile={mobile}>
        {!state ? (
          <Loading label="Загружаю очередь…" />
        ) : (
          <>
            <OfflineNote live={state.live} />
            {state.data.length === 0 && <div style={{ fontSize: 14, color: C.muted }}>Очередь пуста.</div>}
            {STAGES.map((stage) => {
              const posts = state.data.filter((p) => p.stage === stage);
              if (!posts.length) return null;
              // Scheduled/attention posts can be moved; published/publishing show metrics.
              const canReschedule = stage === 'scheduled' || stage === 'attention';
              const canMeasure = stage === 'published' || stage === 'publishing';
              return (
                <div key={stage} style={{ marginBottom: 22 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: STAGE_META[stage].tone, flexShrink: 0 }} />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{STAGE_META[stage].label}</span>
                    <span style={mono({ fontSize: 10, color: C.faint })}>{posts.length}</span>
                    <span style={{ fontSize: 12, color: C.faint, marginLeft: 'auto' }}>{STAGE_META[stage].hint}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {posts.map((p) => (
                      <PostRow
                        key={p.id}
                        post={p}
                        mobile={mobile}
                        onReschedule={canReschedule ? () => setSheet(p) : undefined}
                        metricsEnabled={canMeasure}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </ScreenScroll>

      {sheet && <RescheduleSheet post={sheet} onClose={() => setSheet(null)} onDone={reload} />}
    </>
  );
}

// ── Календарь ────────────────────────────────────────────────────

export function CalendarScreen({ mobile }: { mobile?: boolean }) {
  const [state, setState] = useState<Loaded<PromoPost[]> | null>(null);
  const [sheet, setSheet] = useState<PromoPost | null>(null);

  const reload = useCallback(() => {
    let alive = true;
    loadPosts().then((r) => alive && setState(r));
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => reload(), [reload]);

  // Calendar only shows scheduled posts; buildCalendar filters internally.
  const cells = state ? buildCalendar(state.data) : [];

  return (
    <>
      <ScreenHeader title="Календарь" tag="неделя" mobile={mobile} />
      <ScreenScroll mobile={mobile}>
        {!state ? (
          <Loading label="Загружаю план…" />
        ) : (
          <>
            <OfflineNote live={state.live} />
            <BestTimeHeatmap mobile={mobile} />
            <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden', background: C.white }}>
              {cells.map((d, di) => (
                <div key={di} style={{ display: 'flex', gap: 14, padding: mobile ? '12px 14px' : '14px 16px', ...(di < cells.length - 1 ? { borderBottom: `1px solid ${C.line4}` } : {}) }}>
                  <div style={{ width: mobile ? 46 : 54, flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{d.day}</div>
                    <div style={mono({ fontSize: 10, color: C.faint })}>{d.date}</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                    {d.posts.length ? (
                      d.posts.map((p) => (
                        <div
                          key={p.id}
                          onClick={() => setSheet(p)}
                          title="Перенести"
                          style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: STAGE_META[p.stage].tone }} />
                          <span style={{ fontSize: 13.5, color: C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                          <span style={mono({ fontSize: 9.5, color: C.faint, marginLeft: 'auto', flexShrink: 0 })}>
                            {new Date(p.scheduledAt as string).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: 13, color: C.faint }}>—</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </ScreenScroll>

      {sheet && <RescheduleSheet post={sheet} onClose={() => setSheet(null)} onDone={reload} />}
    </>
  );
}

// ── Каналы ───────────────────────────────────────────────────────

const VIA_LABEL: Record<ChannelInfo['via'], string> = {
  official: 'Официальный API',
  buffer: 'Через Buffer',
  cookies: 'Cookie-сессия',
  manual: 'Вручную',
};

function ChannelCard({ ch, onConnect }: { ch: ChannelInfo; onConnect: (platform: SheetPlatform) => void }) {
  const meta = READINESS_META[ch.readiness];
  // Buffer-routed channels manage through the Buffer directory sheet; the rest
  // open their own connection slot. Manual-only channels surface the same sheet
  // (it shows the "no form yet" hint when there's nothing to configure).
  const sheetTarget: SheetPlatform = ch.via === 'buffer' ? 'buffer' : (ch.platform as SheetPlatform);
  const buttonLabel =
    ch.readiness === 'ready' ? 'Управлять' : ch.readiness === 'manual' ? 'Открыть инструкцию' : 'Подключить';

  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '15px 16px', background: C.white, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: C.sidebar, border: `1px solid ${C.line2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={mono({ fontSize: 12, color: C.ink2 })}>{PLATFORM_GLYPH[ch.platform]}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600 }}>{PLATFORM_LABEL[ch.platform]}</div>
          <div style={mono({ fontSize: 10, color: C.faint })}>{VIA_LABEL[ch.via]}</div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: meta.tone, background: C.chipBg, borderRadius: 8, padding: '4px 10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.tone, flexShrink: 0 }} />
          {meta.label}
        </span>
      </div>
      <div style={{ fontSize: 13, color: C.sub }}>{ch.detail || '—'}</div>
      <button
        type="button"
        onClick={() => onConnect(sheetTarget)}
        style={{ alignSelf: 'flex-start', background: ch.readiness === 'ready' ? 'none' : C.ink, color: ch.readiness === 'ready' ? C.sub : C.white, border: ch.readiness === 'ready' ? `1px solid ${C.line3}` : 'none', borderRadius: 9, padding: '7px 14px', fontFamily: SANS, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

export function ChannelsScreen({ mobile }: { mobile?: boolean }) {
  const [state, setState] = useState<Loaded<ChannelInfo[]> | null>(null);
  const [sheet, setSheet] = useState<SheetPlatform | null>(null);

  const reload = useCallback(() => {
    let alive = true;
    loadChannels().then((r) => {
      if (alive) setState(r);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => reload(), [reload]);

  const groups: { key: ChannelInfo['readiness']; title: string }[] = [
    { key: 'ready', title: 'Готовы' },
    { key: 'setup', title: 'Нужна настройка' },
    { key: 'manual', title: 'Только вручную' },
  ];

  return (
    <>
      <ScreenHeader title="Каналы" tag="подключения" mobile={mobile} />
      <ScreenScroll mobile={mobile}>
        {!state ? (
          <Loading label="Проверяю каналы…" />
        ) : (
          <>
            <OfflineNote live={state.live} />
            {groups.map((g) => {
              const items = state.data.filter((c) => c.readiness === g.key);
              if (!items.length) return null;
              return (
                <div key={g.key} style={{ marginBottom: 22 }}>
                  <MonoLabel>{g.title}</MonoLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                    {items.map((c) => (
                      <ChannelCard key={c.platform} ch={c} onConnect={setSheet} />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </ScreenScroll>

      {sheet && (
        <ConnectSheet
          platform={sheet}
          onClose={() => setSheet(null)}
          onDone={() => {
            // Refresh readiness after a connect/disconnect completes.
            reload();
          }}
        />
      )}
    </>
  );
}
